import React, { useState, forwardRef, useImperativeHandle, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  IconButton,
  Tooltip,
  Paper,
  Box,
  TextField,
  CircularProgress,
  Stack,
  Divider,
  Typography,
  Button,
  ClickAwayListener,
  useTheme,
  alpha
} from '@mui/material';
import { DOMSerializer } from 'prosemirror-model'; // Import DOMSerializer

// Icons
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import SpellcheckIcon from '@mui/icons-material/Spellcheck';
import CompressIcon from '@mui/icons-material/Compress';
import ExpandIcon from '@mui/icons-material/Expand';
import TranslateIcon from '@mui/icons-material/Translate';
import SummarizeIcon from '@mui/icons-material/Summarize';
import SendIcon from '@mui/icons-material/Send';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import { backendClient } from '../../../../api/backend';
import { useTranslation } from 'react-i18next';

// Helper function to serialize a ProseMirror Fragment to an HTML string
const serializeFragmentToHtml = (fragment, schema) => {
  const serializer = DOMSerializer.fromSchema(schema);
  const tempDiv = document.createElement('div');
  tempDiv.appendChild(serializer.serializeFragment(fragment));
  return tempDiv.innerHTML;
};


const AiToolbarButton = forwardRef(({ editor, contractId }, ref) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const contract = useSelector(state => state.contract.contract);
  const currentUser = useSelector(state => state.auth?.user?.user);
  const userRole = (contract?.collaborators || []).find(c => c.id === currentUser?.id)?.role;
  const isLocked = ['PENDING_SIGNATURE', 'SIGNED'].includes(contract?.status) || userRole === 'reviewer';
  const [isOpen, setIsOpen] = useState(false);
  const [toolbarPosition, setToolbarPosition] = useState({ top: 0, left: 0 });
  const [aiInput, setAiInput] = useState('');
  const [isAiProcessing, setIsAiProcessing] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [showValidation, setShowValidation] = useState(false);
  const [lastChangeRange, setLastChangeRange] = useState(null);
  const [lastOperation, setLastOperation] = useState(null);
  const [lastPrompt, setLastPrompt] = useState(null);
  // --- CHANGE: Renamed state to reflect it stores HTML ---
  const [originalHtml, setOriginalHtml] = useState(null);
  const [selectionCoords, setSelectionCoords] = useState(null);

  const buttonRef = useRef(null);
  const inputRef = useRef(null);
  const toolbarRef = useRef(null);

  const isTextSelected = editor && editor.state.selection && !editor.state.selection.empty;

  // Effect to update toolbar position on scroll (No changes needed here)
  useEffect(() => {
    if (!isOpen || !editor) return;
    const handleScroll = () => {
      if (selectionCoords) {
        updateToolbarPosition();
      }
    };
    const scrollContainers = findScrollContainers(editor.view.dom);
    scrollContainers.forEach(container => {
      container.addEventListener('scroll', handleScroll, { passive: true });
    });
    window.addEventListener('resize', handleScroll, { passive: true });
    return () => {
      scrollContainers.forEach(container => {
        container.removeEventListener('scroll', handleScroll);
      });
      window.removeEventListener('resize', handleScroll);
    };
  }, [isOpen, editor, selectionCoords]);

  // Helper to find all scrollable containers (No changes needed here)
  const findScrollContainers = (element) => {
      // ... (implementation remains the same) ...
      const containers = [];
      let current = element;

      while (current && current !== document.body) {
      const style = window.getComputedStyle(current);
      const overflow = style.overflow + style.overflowY + style.overflowX;

      if (/(auto|scroll)/.test(overflow)) {
        containers.push(current);
      }

      current = current.parentElement;
      }
      containers.push(window); // Always add window
      return containers;
  };

  // Update toolbar position based on selection (No changes needed here)
  const updateToolbarPosition = () => {
    // ... (implementation remains the same) ...
    if (!editor || !editor.view || !selectionCoords) return;
    try {
      const { state, view } = editor;
      const { selection } = state;
      if (!selection.empty) {
        const { from, to } = selection;
        const start = view.coordsAtPos(from);
        const end = view.coordsAtPos(to);
        const left = Math.min(start.left, end.left);
        const right = Math.max(start.right, end.right);
        const bottom = Math.max(start.bottom, end.bottom);
        const newCoords = {
          left: left + (right - left) / 2,
          bottom: bottom
        };
        const toolbarWidth = toolbarRef.current ? toolbarRef.current.offsetWidth : 320;
        setToolbarPosition({
          left: newCoords.left - (toolbarWidth / 2),
          top: newCoords.bottom + 8
        });
        setSelectionCoords(newCoords);
      }
    } catch (error) {
      console.error('Error updating toolbar position:', error);
    }
  };

  // Handle clicking outside to close toolbar (Modified for originalHtml)
  const handleClickAway = (event) => {
    if (buttonRef.current && buttonRef.current.contains(event.target)) {
      return;
    }
    if (showValidation) {
      handleAcceptChanges();
    } else {
      handleClose();
    }
  };

  // Expose methods via ref (No changes needed here)
  useImperativeHandle(ref, () => ({
    click: () => handleButtonClick(),
    openAtSelectionPosition: () => openAtSelection()
  }));

  if (!editor) return null;

  // Function to open toolbar at current text selection
  const openAtSelection = () => {
        if (isLocked) return;
        if (!editor || !editor.view) return;
        const { state, view } = editor;
        const { selection } = state;
        if (selection.empty) return;
        const { from, to } = selection;

        // Apply highlight to the selected text
        const selectedText = editor.state.doc.textBetween(from, to);
        editor.commands.highlightRiskText(selectedText, '#273C68'); // Blue color for AI selection

        const start = view.coordsAtPos(from);
        const end = view.coordsAtPos(to);
        const left = Math.min(start.left, end.left);
        const right = Math.max(start.right, end.right);
        const bottom = Math.max(start.bottom, end.bottom);
        setSelectionCoords({
        left: left + (right - left) / 2,
        bottom: bottom
        });
        const toolbarWidth = toolbarRef.current ? toolbarRef.current.offsetWidth : 320; // Recalculate or use state
        setToolbarPosition({
        left: left + (right - left) / 2 - (toolbarWidth / 2),
        top: bottom + 8
        });
        setIsOpen(true);
        setTimeout(() => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
        }, 10);
  };

  // Get cursor coordinates for positioning (No changes needed here)
  const getCursorCoordinates = () => {
    // ... (implementation remains the same) ...
        if (!editor || !editor.view) return null;
        const { state, view } = editor;
        const { selection } = state;
        const { to } = selection; // Use 'to' for cursor position
        try {
        const coords = view.coordsAtPos(to);
        return { left: coords.left, bottom: coords.bottom };
        } catch (error) {
        console.error('Error getting cursor coordinates:', error);
        return null;
        }
  };

  // Handle AI button click (Modified to preserve selection highlight)
  const handleButtonClick = () => {
    if (isLocked) return;
    if (isOpen) {
        handleClose();
        return;
    }

    console.log('selected', editor.state.selection)
    // Get the current selection
    const { from, to, empty } = editor.state.selection;
    if (!empty) {
        // Store the selected text for highlighting
        const selectedText = editor.state.doc.textBetween(from, to);
        // Apply highlight to the selected text
        editor.commands.highlightRiskText(selectedText, '#273C68'); // Blue color for AI selection
    }

    const coords = selectionCoords || getCursorCoordinates(); // Prefer existing selection coords
    if (coords) {
        if (!selectionCoords) setSelectionCoords(coords); // Store if newly calculated
        const toolbarWidth = toolbarRef.current ? toolbarRef.current.offsetWidth : 320;
        setToolbarPosition({
            left: coords.left - (toolbarWidth / 2), // Center based on coords
            top: coords.bottom + 8
        });
        setIsOpen(true);
        setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.focus();
            }
        }, 10);
    }
  };

  // Close the toolbar and reset state (Modified to preserve selection highlight)
  const handleClose = () => {
    setIsOpen(false);
    setAiInput('');
    setAiError(null);
    setShowValidation(false);
    setLastChangeRange(null);
    setLastOperation(null);
    setLastPrompt(null);
    setOriginalHtml(null);
    setSelectionCoords(null);

    // Only clear the highlight if we're not in validation mode
    if (!showValidation && editor.commands.clearRiskHighlight) {
        editor.commands.clearRiskHighlight();
    }
  };

  // Handle AI prompt submission (No changes needed here)
  const handleAiSubmit = async () => {
    if (!aiInput.trim()) return;
    await processAiOperation('custom', aiInput);
  };

  // Highlight added text (Modified to calculate range *after* insertion)
  const highlightAddedText = (originalFrom, insertedContentLength) => {
      // Calculate the range *after* the potentially complex HTML insertion
      const finalTo = originalFrom + insertedContentLength; // This might be less accurate with HTML
      // Alternative: Get selection end after insertion if command updates it
      // const finalTo = editor.state.selection.to;

      // Ensure 'to' doesn't exceed document size
      const docSize = editor.state.doc.content.size;
      const safeTo = Math.min(finalTo, docSize);
      const safeFrom = Math.min(originalFrom, safeTo); // Ensure from <= to

      if (safeFrom >= safeTo) {
         console.warn("Highlight range is invalid or empty:", { safeFrom, safeTo });
         return; // Don't try to highlight if range is invalid
      }


      // Save the actual range of the change
      setLastChangeRange({ from: safeFrom, to: safeTo });

      try {
          // Highlight based on the *new* range
          // The highlighting command might need adjustment depending on how it works
          // If it relies purely on text, it might not style HTML correctly.
          // If it applies marks, it should work better.
          if (editor.commands.highlightRiskText) {
              // Get the text within the *new* range for the command if it needs it
              const addedText = editor.state.doc.textBetween(safeFrom, safeTo, '\n');
              // console.log("Highlighting range:", { from: safeFrom, to: safeTo, text: addedText });
              editor.commands.highlightRiskText(addedText, '#273C68'); // Blue color
          } else {
              console.warn('RiskHighlight extension or highlightRiskText command not found.');
          }
      } catch (error) {
          console.error('Error highlighting added text:', error);
      }
  };

  // Handle accepting AI changes (Modified to clear highlight after accepting)
  const handleAcceptChanges = () => {
    if (editor.commands.clearRiskHighlight) {
        editor.commands.clearRiskHighlight();
    }
    setShowValidation(false);
    handleClose(); // Close toolbar after accepting
  };

  // Handle rejecting AI changes (Modified to clear highlight after rejecting)
  const handleRejectChanges = () => {
    try {
        // Restore original HTML if we have the information
        if (lastChangeRange && originalHtml !== null) {
            const { from, to } = lastChangeRange;

            // Ensure range is valid before attempting deletion/insertion
            const docSize = editor.state.doc.content.size;
            const safeFrom = Math.min(from, docSize);
            const safeTo = Math.min(to, docSize);

            if (safeFrom <= safeTo) {
                editor.chain().focus()
                    .deleteRange({ from: safeFrom, to: safeTo })
                    .insertContentAt(safeFrom, originalHtml)
                    .run();
            } else {
                console.warn("Invalid range for reject:", { from, to, docSize });
                editor.commands.undo();
            }
        } else {
            editor.commands.undo();
        }

        if (editor.commands.clearRiskHighlight) {
            editor.commands.clearRiskHighlight();
        }
        setShowValidation(false);
        handleClose();
    } catch (error) {
        console.error('Error rejecting changes:', error);
    }
  };

  // Generate a new AI response (Modified to use originalHtml and handle HTML response)
  const handleRefreshChanges = async () => {
    // --- CHANGE: Use originalHtml ---
    if (!originalHtml || !lastOperation || !lastChangeRange) {
        console.warn("Cannot refresh: Missing original HTML, operation, or range.");
        return; // Can't refresh without original context
    }

    setIsAiProcessing(true); // Indicate processing starts
    setAiError(null); // Clear previous errors

    try {
        // Clear previous highlight immediately
        if (editor.commands.clearRiskHighlight) {
        editor.commands.clearRiskHighlight();
        }

        // Call backend with the *original HTML*
        const response = await backendClient.processPrompt(
            // --- CHANGE: Send originalHtml ---
            originalHtml,
            lastOperation,
            lastOperation === 'custom' ? lastPrompt : null,
            contractId
        );

        // --- CHANGE: Assume response contains HTML in processed_text ---
        // Adjust 'processed_text' if the backend returns HTML under a different key (e.g., 'processed_html')
        const resultHtml = response.processed_text;

        // Replace content with the new HTML result
        const { from } = lastChangeRange; // 'to' is less relevant now, we replace based on 'from'

        // Ensure 'from' is valid before insertion
        const docSize = editor.state.doc.content.size;
        const safeFrom = Math.min(from, docSize);

        // Perform the replacement
        editor.chain().focus()
            // --- CHANGE: Delete old range, insert new HTML ---
            .deleteRange(lastChangeRange) // Delete the previously inserted content range
            .insertContentAt(safeFrom, resultHtml) // Insert the new HTML at the start position
            .run();

        // --- CHANGE: Recalculate range *after* insertion for highlighting ---
        // Get the actual length of the inserted content (difficult for pure HTML string)
        // A pragmatic approach: estimate based on string length, or better,
        // create a temporary node/fragment to measure, or get selection end post-insert.
        // Let's use the post-insertion selection end.

        // Force focus and get the selection end which *should* be after the inserted content
        const currentSelectionEnd = editor.state.selection.to;
        const newRange = { from: safeFrom, to: currentSelectionEnd };


        // Update lastChangeRange *before* highlighting
        setLastChangeRange(newRange);

        // Highlight the newly added HTML content based on the *new* range
        if (editor.commands.highlightRiskText) {
            const addedText = editor.state.doc.textBetween(newRange.from, newRange.to, '\n');
            editor.commands.highlightRiskText(addedText, '#273C68'); // Blue color
        }

        // Update toolbar position as content changed
        updateToolbarPosition();

    } catch (error) {
        console.error('Error refreshing changes:', error);
        setAiError(`Error: ${error.message || 'Failed to generate new response'}`);
        // Decide if we should revert or leave the editor state as is on error
    } finally {
        setIsAiProcessing(false); // Indicate processing finished
    }
};


  // Process AI operation (Modified to preserve selection highlight and ensure added text is highlighted)
  const processAiOperation = async (operationType, customPrompt = '') => {
    setIsAiProcessing(true);
    setAiError(null);
    setShowValidation(false); // Hide validation from previous runs

    setLastOperation(operationType);
    setLastPrompt(customPrompt);

    try {
        const { state } = editor;
        const { selection } = state;
        const { from, to, empty } = selection;
        const { schema } = state; // Get schema for serializer

        let selectedHtml = '';
        let rangeToReplace = { from, to };

        // Store the original selection range for highlighting
        const originalSelectionRange = { from, to };

        // --- CHANGE: Extract HTML instead of text ---
        if (!empty) {
            // Get the selected slice and serialize its content to HTML
            const slice = selection.content();
            selectedHtml = serializeFragmentToHtml(slice.content, schema);
            rangeToReplace = { from, to }; // Use selection range
        } else {
            // Get the current node (e.g., paragraph) the cursor is in
            const { $from } = selection;
            const node = $from.parent; // Get the parent node (e.g., paragraph)
            if (node && node.isBlock) { // Check if it's a block node
                const nodeFrom = $from.start($from.depth); // Start position of the node
                const nodeTo = $from.end($from.depth);     // End position of the node
                const nodeFragment = state.doc.content.cut(nodeFrom, nodeTo); // Get the fragment for the node
                selectedHtml = serializeFragmentToHtml(nodeFragment, schema); // Serialize the node's content
                rangeToReplace = { from: nodeFrom, to: nodeTo }; // Use node's range
            }
        }

        // Store the original HTML
        setOriginalHtml(selectedHtml);

        // Don't process empty content
        if (!selectedHtml.trim()) {
            setAiError('Please select content or place cursor in a paragraph with content.');
            setIsAiProcessing(false);
            return;
        }

        // Call the backend API with HTML
        const response = await backendClient.processPrompt(
            selectedHtml,
            operationType,
            operationType === 'custom' ? customPrompt : null,
            contractId
        );

        // Get the processed HTML from the response
        const resultHtml = response.processed_text;

        // Store the original selection range before making changes
        const originalFrom = rangeToReplace.from;

        // Replace content using the determined range and insert HTML
        editor.chain().focus()
            .deleteRange(rangeToReplace) // Delete the original selection/node
            .insertContentAt(rangeToReplace.from, resultHtml) // Insert the new HTML
            .run();

        // Get the final position after insertion
        const finalSelectionEnd = editor.state.selection.to;
        const insertedRange = { from: originalFrom, to: finalSelectionEnd };

        // Update lastChangeRange for potential undo/reject operations
        setLastChangeRange(insertedRange);

        // Highlight the newly added text
        highlightAddedText(originalFrom, finalSelectionEnd - originalFrom);

        // Update toolbar position as content and selection may have changed
        updateToolbarPosition();

        // Show validation options
        setShowValidation(true);
    } catch (error) {
        console.error('AI operation failed:', error);
        setAiError(`Error: ${error.message || 'Failed to process content'}`);
        setOriginalHtml(null); // Clear original HTML on error
    } finally {
        setIsAiProcessing(false);
    }
  };

  // AI action button component (No changes needed here)
  const AiAction = ({ tooltip, icon, action, disabled }) => (
    // ... (implementation remains the same) ...
     <Tooltip title={tooltip}>
      <span> {/* Span needed for disabled Tooltip */}
        <IconButton
          size="small"
          onClick={() => processAiOperation(action)}
          disabled={disabled || isAiProcessing || !isTextSelected /* Disable if no text selected */}
          sx={{
            color: 'text.secondary',
            '&:hover': {
              color: 'primary.main',
              bgcolor: 'action.hover'
            },
            '&.Mui-disabled': { // Style disabled state if needed
                 color: 'action.disabled',
            }
          }}
        >
          {icon}
        </IconButton>
      </span>
    </Tooltip>
  );

  // --- RETURN JSX --- (Structure remains the same, logic inside uses updated state/functions)
  return (
    <>
      {/* AI Button */}
       <Tooltip title={isLocked ? (userRole === 'reviewer' ? t('reviewer_locked_tooltip', 'Vous êtes en mode relecture') : t('contract_locked_tooltip', 'Contract is locked for signature')) : t('ai_assistant_tooltip')}>
        {/* Tooltip updated with translation */}
        <span> {/* Span needed for disabled Tooltip */}
            <IconButton
            ref={buttonRef}
            onClick={handleButtonClick}
            disabled={isLocked}
            size="small"
            color={isOpen ? "secondary" : "primary"} // Change color when open?
            sx={{ p: 0.5 }}
            >
            <DriveFileRenameOutlineIcon fontSize="small" />
            </IconButton>
        </span>
        </Tooltip>


      {/* AI Toolbar Popup */}
      {isOpen && (
        <ClickAwayListener onClickAway={handleClickAway}>
          {/* Positioning div */}
          <div style={{
             position: 'fixed', // Use fixed to position relative to viewport
             zIndex: 1301, // Ensure it's above editor and other elements
             left: `${toolbarPosition.left}px`,
             top: `${toolbarPosition.top}px`,
             width: 320, // Fixed width
             // Removed border styling here, Paper handles it
          }}>
            <Paper
              ref={toolbarRef}
              className="ai-toolbar-paper"
              elevation={6}
              sx={{
                width: '100%',
                borderRadius: '16px',
                overflow: 'hidden',
                boxShadow: isDark
                  ? `0 4px 20px ${alpha(theme.palette.common.black, 0.5)}`
                  : `0 4px 20px ${alpha(theme.palette.common.black, 0.15)}`,
                border: `1px solid ${theme.palette.divider}`,
                backdropFilter: 'blur(5px)',
                backgroundColor: theme.palette.background.paper,
              }}
            >
              {/* Validation UI */}
              {showValidation ? (
                <Box sx={{ /* Styling for validation box */
                    p: 1.5,
                    // bgcolor: 'rgba(240, 248, 255, 0.8)', // Example: Alice Blue background
                    // backdropFilter: 'blur(8px)', // Inherit or set specific
                    maxWidth: '100%'
                }}>
                  <Typography /* Styling for message */
                     variant="body2" sx={{ mb: 1.5, fontWeight: 500, fontSize: '0.875rem', color: 'text.primary', display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}
                  >
                    {t('changes_ai_msg')} {/* Using translation */}
                  </Typography>
                  <Stack direction="row" spacing={1} justifyContent="flex-end"> {/* Pushed to end */}
                    {/* Reject Button */}
                    <Button /* Styling */
                        variant="outlined" size="small" startIcon={<CloseIcon fontSize="small" />} onClick={handleRejectChanges} sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.8125rem', fontWeight: 500, padding: '4px 10px', borderColor: 'divider', color: 'text.secondary', '&:hover': { borderColor: 'text.disabled', bgcolor: 'action.hover' } }}
                    >
                      {t('reject')}
                    </Button>
                     {/* Refresh Button */}
                     <Button /* Styling */
                        variant="outlined" size="small" startIcon={isAiProcessing ? <CircularProgress size={14} color="inherit"/> : <RefreshIcon fontSize="small" />} onClick={handleRefreshChanges} disabled={isAiProcessing} sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.8125rem', fontWeight: 500, padding: '4px 10px', borderColor: 'divider', color: 'text.secondary', '&:hover': { borderColor: 'text.disabled', bgcolor: 'action.hover' }, '&.Mui-disabled': { borderColor: 'divider', color: 'text.disabled' } }}
                     >
                        {t('refresh')}
                     </Button>
                    {/* Accept Button */}
                    <Button /* Styling */
                        variant="contained" size="small" color="primary" startIcon={<CheckIcon fontSize="small" />} onClick={handleAcceptChanges} sx={{ bgcolor: isDark ? 'grey.100' : 'grey.900', color: isDark ? 'grey.900' : 'grey.100', borderRadius: 1, textTransform: 'none', fontSize: '0.8125rem', fontWeight: 500, padding: '4px 10px', '&:hover': { bgcolor: isDark ? 'grey.200' : 'grey.800' }, boxShadow: 'none', transition: 'all 0.2s ease' }}
                    >
                      {t('accept')}
                    </Button>
                  </Stack>
                </Box>
              ) : (
                // Input UI
                <Box sx={{ p: 1 }}>
                   <TextField /* Styling and Props */
                     fullWidth
                     inputRef={inputRef} // Assign ref
                     variant="outlined"
                     placeholder={t('ask_ai_placeholder')}
                     value={aiInput}
                     onChange={(e) => setAiInput(e.target.value)}
                     disabled={isAiProcessing}
                     size="small"
                     InputProps={{
                       sx: { pr: 0.5, fontSize: '0.875rem', borderRadius: '12px' },
                       endAdornment: (
                         <IconButton color="primary" size="small" disabled={!aiInput.trim() || isAiProcessing} onClick={handleAiSubmit} sx={{ ml: 0.5 }}>
                           <SendIcon fontSize="small" />
                         </IconButton>
                       ),
                     }}
                     onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAiSubmit(); }}}
                   />
                  {/* Error Message */}
                  {aiError && (
                    <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5, ml: 0.5, fontSize: '0.7rem' }}>
                      {aiError}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Action Buttons (only show when not validating/processing) */}
              {!showValidation && !isAiProcessing && (
                <>
                  <Divider sx={{ my: 0.5 }} /> {/* Divider with margin */}
                  <Stack
                    direction="row"
                    spacing={0.5} // Reduced spacing
                    sx={{ p: 0.5, flexWrap: 'wrap', justifyContent: 'space-around' /* Even distribution */ }}
                  >
                    <AiAction tooltip={t('improve')} icon={<AutoFixHighIcon fontSize="small" />} action="improve" disabled={!isTextSelected}/>
                    <AiAction tooltip={t('spelling')} icon={<SpellcheckIcon fontSize="small" />} action="spelling" disabled={!isTextSelected}/>
                    <AiAction tooltip={t('shorter')} icon={<CompressIcon fontSize="small" />} action="shorter" disabled={!isTextSelected}/>
                    <AiAction tooltip={t('longer')} icon={<ExpandIcon fontSize="small" />} action="longer" disabled={!isTextSelected}/>
                    <AiAction tooltip={t('translate')} icon={<TranslateIcon fontSize="small" />} action="translate" disabled={!isTextSelected}/>
                    <AiAction tooltip={t('summarize')} icon={<SummarizeIcon fontSize="small" />} action="summarize" disabled={!isTextSelected}/>
                  </Stack>
                </>
              )}

              {/* Global Processing Indicator (shown below actions or input) */}
              {isAiProcessing && !showValidation && ( // Only show if not already in validation view
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 1, height: 40 /* Fixed height */}}>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      <Typography variant="caption" color="textSecondary">{t('processingText')}</Typography>
                  </Box>
              )}

            </Paper>
          </div>
        </ClickAwayListener>
      )}
    </>
  );
});

export default AiToolbarButton;